import Phaser from 'phaser';
import { CANVAS_WIDTH, CANVAS_HEIGHT } from '@/config/GameConfig';

/**
 * Splash screen: shows studio logos (LIVA, Probably Playable) with
 * fade-in/out transitions and a PS1-style chime before the title screen.
 * Skippable via click or any key.
 */
export class SplashScene extends Phaser.Scene {
  private skipped = false;

  constructor() {
    super({ key: 'SplashScene' });
  }

  create(): void {
    this.skipped = false;
    this.cameras.main.setBackgroundColor('#ffffff');

    // Order: Probably Playable first, then LIVA
    const logos = ['logo_probably_playable', 'logo_liva'];
    const labels = ['Produced by', 'Boosted by'];

    // Skip on click or key
    const skip = () => {
      if (this.skipped) return;
      this.skipped = true;
      this.tweens.killAll();
      this.time.removeAllEvents();
      this.scene.start('TitleScene');
    };
    this.input.on('pointerdown', skip);
    this.input.keyboard?.on('keydown', skip);

    this.showSequence(logos, labels, 0);
  }

  private showSequence(logos: string[], labels: string[], idx: number): void {
    if (idx >= logos.length || this.skipped) {
      if (!this.skipped) this.scene.start('TitleScene');
      return;
    }

    const logoY = CANVAS_HEIGHT / 2 + 10; // shift logo down slightly to make room for label
    const logo = this.add.image(CANVAS_WIDTH / 2, logoY, logos[idx]);
    logo.setAlpha(0);

    // Scale to fit 60% of canvas, never upscale
    const maxW = CANVAS_WIDTH * 0.6;
    const maxH = CANVAS_HEIGHT * 0.6;
    const scale = Math.min(maxW / logo.width, maxH / logo.height, 1) * 1.2;
    logo.setScale(scale);

    // LINEAR filter so logos aren't pixelated (game has pixelArt:true)
    const tex = this.textures.get(logos[idx]);
    if (tex?.source?.length) {
      tex.setFilter(Phaser.Textures.FilterMode.LINEAR);
    }

    // Label above the logo
    const labelY = 200;
    const label = this.add.text(CANVAS_WIDTH / 2, labelY, labels[idx], {
      fontFamily: 'monospace',
      fontSize: '20px',
      color: '#000000',
      align: 'center',
    }).setOrigin(0.5).setAlpha(0).setDepth(10);

    this.playPS1Chime();

    const targets = [logo, label];

    // Fade in 1s → hold 2s → fade out 1s → next
    this.tweens.add({
      targets,
      alpha: 1,
      duration: 1000,
      ease: 'Sine.easeIn',
      onComplete: () => {
        this.time.delayedCall(2000, () => {
          this.tweens.add({
            targets,
            alpha: 0,
            duration: 1000,
            ease: 'Sine.easeOut',
            onComplete: () => {
              logo.destroy();
              label.destroy();
              this.showSequence(logos, labels, idx + 1);
            },
          });
        });
      },
    });
  }

  /** PS1-style ascending shimmer: C5-E5-G5 major chord + high overtone */
  private playPS1Chime(): void {
    const ctx = new AudioContext();
    const now = ctx.currentTime;

    const master = ctx.createGain();
    master.gain.setValueAtTime(0.3, now);
    master.gain.exponentialRampToValueAtTime(0.01, now + 3);
    master.connect(ctx.destination);

    // Staggered major chord tones
    const tones: [number, number][] = [
      [523.25, 0],     // C5
      [659.25, 0.15],  // E5
      [783.99, 0.3],   // G5
    ];

    for (const [freq, delay] of tones) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + delay);
      osc.frequency.exponentialRampToValueAtTime(freq * 1.02, now + delay + 2);
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.25, now + delay + 0.1);
      gain.gain.exponentialRampToValueAtTime(0.001, now + delay + 2.5);
      osc.connect(gain);
      gain.connect(master);
      osc.start(now + delay);
      osc.stop(now + delay + 3);
    }

    // High shimmer overtone G6 → C7
    const shimmer = ctx.createOscillator();
    const sGain = ctx.createGain();
    shimmer.type = 'sine';
    shimmer.frequency.setValueAtTime(1567.98, now + 0.3);
    shimmer.frequency.exponentialRampToValueAtTime(2093.0, now + 2.5);
    sGain.gain.setValueAtTime(0, now);
    sGain.gain.linearRampToValueAtTime(0.08, now + 0.5);
    sGain.gain.exponentialRampToValueAtTime(0.001, now + 3);
    shimmer.connect(sGain);
    sGain.connect(master);
    shimmer.start(now + 0.3);
    shimmer.stop(now + 3.5);

    setTimeout(() => ctx.close(), 4000);
  }
}
